import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateAiProvider } from '@/lib/api/generated/api';

import { toAiProviderError } from '../errors';
import { aiProviderKeys } from '../queryKeys';
import type { AiProvider, AiProviderWriteInput } from '../types';

/**
 * Edit an existing AI Provider.
 *
 * `body.token` being omitted leaves the stored token unchanged (spec FR-007)
 * — that contract is enforced server-side (via `model_fields_set`); this hook
 * only forwards whatever the form sent, and never sends `provider_type`
 * (absent from `UpdateAIProviderRequest` by design — changing type after
 * creation is not supported).
 */
export function useUpdateAiProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AiProviderWriteInput;
    }): Promise<AiProvider> => {
      const response = await updateAiProvider(id, {
        name: body.name,
        base_url: body.base_url,
        token: body.token,
      });
      if (response.status === 200) return response.data;
      throw toAiProviderError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiProviderKeys.all() });
    },
  });
}
